/**
 * Settings hub navigation (Sept 2026 restructure).
 *
 * The sidebar shows a single "Settings" entry; the hub's left menu lists these
 * entries by category. Several entries merge former pages as tabs, but every
 * original page id stays a real, separately permissioned page: opening any of
 * a merged entry's ids renders the merged page on that id's tab, and a tab is
 * shown only when the user can open its id. So Page Visibility, stored lists,
 * server gates and deep links are unchanged.
 */

export const SETTINGS_CATEGORIES = [
  { id: 'people', label: 'People & access' },
  { id: 'communications', label: 'Correspondence' },
  { id: 'documents', label: 'Documents & AI' },
  { id: 'business', label: 'Business setup' },
  { id: 'code', label: 'Kept in code' },
  { id: 'clientTools', label: 'Client tools' },
  { id: 'developer', label: 'Developer' },
];

/**
 * key:      stable entry key (also the merged page key)
 * ids:      page ids this entry covers, in tab order; the first id the user can
 *           open is where the menu entry navigates
 * aliasIds: ids that open this entry but are not their own tab (legacy aliases)
 */
export const SETTINGS_ENTRIES = [
  {
    key: 'peopleAccess',
    category: 'people',
    label: 'People & access',
    icon: 'Users',
    description: 'Teams, members and roles; which pages each role can open; default client-list filters.',
    ids: ['adminTeams', 'adminPageVisibility', 'adminDefaultFilters'],
  },
  {
    key: 'clientAllocation',
    category: 'people',
    label: 'Client Allocation',
    icon: 'Shuffle',
    description: 'Which onboarding adviser gets each new client: off, by hand, or automatically by ratio.',
    ids: ['adminClientAllocation'],
  },
  {
    key: 'guidedSandbox',
    category: 'documents',
    label: 'Para Sandbox',
    icon: 'FlaskConical',
    description: 'Try AI drafts, document mark-up and the chat on anonymised copies of real clients, apart from live clients.',
    ids: ['adminGuidedSandbox'],
  },
  {
    key: 'communications',
    category: 'communications',
    label: 'Correspondence',
    icon: 'Mail',
    description: 'Every email, DocuSign envelope, calendar invite and SMS — who receives it and what it says — plus phone numbers, the upload link SMS, scheduled SMS and the DocuSign connection.',
    // adminSystemEmails keeps its Page Visibility row; it opens the Correspondence tab (wording + recipients moved there).
    ids: ['adminCorrespondence', 'adminSmsNumbers', 'adminIdUploadSms', 'adminSmsScheduling', 'adminDocusignSettings', 'adminSystemEmails'],
  },
  {
    key: 'documentTemplates',
    category: 'documents',
    label: 'Document Templates',
    icon: 'Files',
    description: 'Amendment requests on drafted documents, the Document Storage switch and document testing.',
    ids: ['adminDocuments', 'adminDocumentQa', 'adminArDocumentTest'],
    aliasIds: ['adminDocumentLayout', 'adminContentBlocks', 'adminDocumentStyling', 'adminChartSettings', 'adminDocxTemplates'],
  },
  {
    key: 'settingsInCode',
    category: 'code',
    label: 'Settings in code',
    icon: 'Braces',
    description: 'Every setting kept in the repo instead of a page: chart colours, each firm\u2019s document look, templates, wording and prompts, read-only, with how to change each.',
    ids: ['adminSettingsInCode'],
    // Business Setup → Assumptions (retired Oct 23 2026) opens here.
    aliasIds: ['adminAssumptions'],
  },
  {
    key: 'aiPrompts',
    category: 'documents',
    label: 'AI Prompts',
    icon: 'Sparkles',
    description: 'Claude usage, and the model in use (kept in code).',
    ids: ['adminAiPrompts'],
  },
  {
    key: 'portfolios',
    category: 'business',
    label: 'Portfolios',
    icon: 'TrendingUp',
    description: 'Retail platform options, fee schedules and industry funds.',
    ids: ['adminPortfolios'],
  },
  {
    key: 'formConfig',
    category: 'business',
    label: 'Form Configuration',
    icon: 'ClipboardList',
    description: 'Required Fact Find fields and the order of Fact Find and Recommendations sections.',
    ids: ['adminFormConfig'],
  },

  {
    key: 'workflows',
    category: 'business',
    label: 'Workflows',
    icon: 'Workflow',
    description: 'Workflow templates and trigger rules for each firm.',
    ids: ['adminWorkflows'],
  },
  {
    key: 'banner',
    category: 'business',
    label: 'Banner',
    icon: 'Megaphone',
    description: 'The announcement shown across the top of the app.',
    ids: ['adminBanner'],
  },
  {
    key: 'clientOverrides',
    category: 'clientTools',
    label: 'Client overrides',
    icon: 'UserCog',
    description: "Correct a client's assignment, teams and dates, merge notes, duplicate or delete; inspect the raw record.",
    ids: ['adminManualOverride', 'adminParaplannerOverride', 'adminClientJson'],
  },
  {
    key: 'marketingImports',
    category: 'clientTools',
    label: 'Marketing Imports',
    icon: 'Upload',
    description: 'Log of marketing CSV and API imports and their outcomes.',
    ids: ['adminMarketingImports'],
  },
  {
    key: 'deployments',
    category: 'developer',
    label: 'Deployments',
    icon: 'Rocket',
    description: 'Which build is live in this tab, on Firebase and on Railway.',
    ids: ['adminDeployments'],
  },
];

const ENTRY_BY_TAB = new Map();
SETTINGS_ENTRIES.forEach((entry) => {
  [...entry.ids, ...(entry.aliasIds || [])].forEach((id) => ENTRY_BY_TAB.set(id, entry));
});

/** Every page id that lives inside the Settings hub (including legacy aliases). */
export const SETTINGS_TAB_IDS = [...ENTRY_BY_TAB.keys()];

export function isSettingsTab(tabName) {
  return ENTRY_BY_TAB.has(tabName);
}

export function getSettingsEntryForTab(tabName) {
  return ENTRY_BY_TAB.get(tabName) || null;
}

/** Ids of `entry` the user can open (tab order). */
export function accessibleEntryIds(entry, canAccess) {
  return entry.ids.filter((id) => canAccess(id));
}

/** Menu model for the hub: categories with the entries this user can open. */
export function getSettingsMenu(canAccess) {
  return SETTINGS_CATEGORIES.map((category) => ({
    ...category,
    entries: SETTINGS_ENTRIES
      .filter((entry) => entry.category === category.id)
      .map((entry) => ({ ...entry, targetTab: accessibleEntryIds(entry, canAccess)[0] || null }))
      .filter((entry) => entry.targetTab),
  })).filter((category) => category.entries.length > 0);
}

/** Where the sidebar's Settings entry goes (first accessible entry in menu order). */
export function getFirstAccessibleSettingsTab(canAccess) {
  for (const entry of SETTINGS_ENTRIES) {
    const id = accessibleEntryIds(entry, canAccess)[0];
    if (id) return id;
  }
  return null;
}
